import { useMemo, useCallback } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  Select,
  VStack,
} from '@chakra-ui/react';

import SelectSearch from 'components/fields/SelectSearch';
import { useAnswerCodes, useValidationSchema } from '../../hooks';
import { ValidatedField } from '../../components';
import type { DriverOccupationProps } from './types';

const DriverOccupation = ({
  driver,
  index,
  onNestedChange,
}: DriverOccupationProps) => {
  const occupation = driver.occupation;
  const { getOptions, getCode, isLoading, getOccupationOtherCodes } =
    useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();
  const occupationOptions = getOptions('occupation');
  const occupationOtherCodes = getOccupationOtherCodes();
  const showOccupationOther = occupationOtherCodes.includes(
    occupation.occupation,
  );

  const isRequired = (field: string) =>
    isFieldRequired(
      'driver',
      field,
      driver as unknown as Record<string, unknown>,
    );

  const studentOccupationCode = useMemo(() => {
    const studentOption = occupationOptions.find(
      (opt) => opt.label.toLowerCase() === 'student',
    );
    return studentOption?.value ?? null;
  }, [occupationOptions]);

  const retiredOccupationCode = useMemo(() => {
    const retiredOption = occupationOptions.find(
      (opt) => opt.label.toLowerCase() === 'retired',
    );
    return retiredOption?.value ?? null;
  }, [occupationOptions]);

  const isStudent =
    studentOccupationCode !== null &&
    occupation.occupation === studentOccupationCode;

  const isRetiredOccupation =
    retiredOccupationCode !== null &&
    occupation.occupation === retiredOccupationCode;

  const hasNoOccupation = !occupation.occupation;
  const showRetiredCheckbox = hasNoOccupation || isRetiredOccupation;

  const isStudentAway = occupation.student_away_at_school === true;

  const handleOccupationChange = useCallback(
    (value: string | number) => {
      const newValue = String(value ?? '');
      const shouldClearOther = !occupationOtherCodes.includes(newValue);

      if (retiredOccupationCode && newValue === retiredOccupationCode) {
        onNestedChange(index, 'occupation', {
          occupation: newValue,
          retired: true,
          ...(shouldClearOther && { occupation_other: '' }),
        });
      } else if (occupation.retired) {
        onNestedChange(index, 'occupation', {
          occupation: newValue,
          retired: false,
          ...(shouldClearOther && { occupation_other: '' }),
        });
      } else if (shouldClearOther && occupation.occupation_other) {
        onNestedChange(index, 'occupation', {
          occupation: newValue,
          occupation_other: '',
        });
      } else {
        onNestedChange(index, 'occupation', 'occupation', newValue);
      }
    },
    [
      index,
      onNestedChange,
      retiredOccupationCode,
      occupation.retired,
      occupation.occupation_other,
      occupationOtherCodes,
    ],
  );

  const handleRetiredChange = useCallback(
    (checked: boolean) => {
      if (checked && retiredOccupationCode) {
        onNestedChange(index, 'occupation', {
          occupation: retiredOccupationCode,
          retired: true,
        });
      } else {
        onNestedChange(index, 'occupation', {
          occupation: '',
          retired: false,
        });
      }
    },
    [index, onNestedChange, retiredOccupationCode],
  );

  return (
    <VStack spacing={2} align="stretch">
      <FormControl
        isRequired={isRequired('occupation.occupation')}
        isInvalid={
          isRequired('occupation.occupation') && !occupation.occupation
        }
      >
        <FormLabel fontSize="xs" whiteSpace="nowrap">
          Occupation
        </FormLabel>
        <SelectSearch
          options={occupationOptions}
          value={getCode(occupation.occupation, 'occupation')}
          onChange={handleOccupationChange}
          placeholder="Search occupation..."
          isDisabled={false}
          isLoading={isLoading}
          isInvalid={
            isRequired('occupation.occupation') && !occupation.occupation
          }
        />
      </FormControl>

      {showOccupationOther && (
        <FormControl>
          <FormLabel fontSize="xs" whiteSpace="nowrap">
            Other Occupation
          </FormLabel>
          <Input
            variant="main"
            size="sm"
            value={occupation.occupation_other ?? ''}
            onChange={(e) =>
              onNestedChange(
                index,
                'occupation',
                'occupation_other',
                e.target.value,
              )
            }
            isDisabled={false}
            placeholder="Specify occupation"
          />
        </FormControl>
      )}

      {isStudent && (
        <>
          <FormControl>
            <FormLabel fontSize="xs" whiteSpace="nowrap">
              Student Away At School
            </FormLabel>
            <Select
              variant="main"
              size="sm"
              value={occupation.student_away_at_school ? 'yes' : 'no'}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'occupation',
                  'student_away_at_school',
                  e.target.value === 'yes',
                )
              }
              isDisabled={false}
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </Select>
          </FormControl>

          {isStudentAway && (
            <ValidatedField
              itemPath="driver"
              fieldPath="occupation.student_away_km"
              label="Distance From Home (Kms)"
              isDisabled={false}
              labelSize="xs"
            >
              <Input
                variant="main"
                size="sm"
                inputMode="numeric"
                pattern="[0-9]*"
                value={occupation.student_away_km ?? ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  onNestedChange(
                    index,
                    'occupation',
                    'student_away_km',
                    raw ? parseInt(raw, 10) : null,
                  );
                }}
                placeholder="Distance in km"
              />
            </ValidatedField>
          )}

          <Checkbox
            isChecked={occupation.good_student_discount}
            onChange={(e) =>
              onNestedChange(
                index,
                'occupation',
                'good_student_discount',
                e.target.checked,
              )
            }
            isDisabled={false}
            size="sm"
          >
            Good Student Discount
          </Checkbox>
        </>
      )}

      {showRetiredCheckbox && (
        <Checkbox
          isChecked={occupation.retired}
          onChange={(e) => handleRetiredChange(e.target.checked)}
          isDisabled={false}
          size="sm"
        >
          Retired
        </Checkbox>
      )}
    </VStack>
  );
};

export default DriverOccupation;
